// ═══════════════════════════════════════════════════════════════════════════════
// Built-in roles — the identity tiers sourced from Entra ID.
//
// These are NOT custom RBAC roles: they answer "who is this user" (staff tier
// vs external customer), while custom roles in the RBAC matrix answer "which
// views can they touch". Built-in roles are derived server-side on every login
// (api/functions/me.ts deriveRole) from Entra app roles / group membership /
// env overrides — they cannot be created, edited, or deleted in the app.
// ═══════════════════════════════════════════════════════════════════════════════
import type { UserRole } from '../types';
import { ROLE_VISIBLE_SECTIONS } from './featureVisibility';

/** Built-in roles that bypass all per-view RBAC checks (frontend mirror of
 *  the same list in api/shared/auth.ts — keep the two in sync). */
export const PRIVILEGED_ROLES: readonly UserRole[] = ['admin', 'lead'];

export function isPrivilegedRole(role: string | undefined): boolean {
  return PRIVILEGED_ROLES.includes(role as UserRole);
}

export interface BuiltInRoleInfo {
  id: UserRole;
  name: string;
  /** Key into the theme palette — resolved to a concrete color where rendered. */
  colorKey: 'yellow' | 'purple' | 'textSecondary' | 'blue';
  description: string;
}

/** Human-readable summary of what a role's ROLE_VISIBLE_SECTIONS whitelist
 *  actually grants, so the Admin UI never drifts from the real config. */
function sectionSummary(role: UserRole): string {
  const config = ROLE_VISIBLE_SECTIONS[role];
  if (!config) return 'every section';
  return Object.entries(config)
    .map(([section, access]) => access === true ? section : `${section} (${access.join(', ')})`)
    .join('; ');
}

export const BUILT_IN_ROLES: readonly BuiltInRoleInfo[] = [
  {
    id: 'admin',
    name: 'Admin',
    colorKey: 'yellow',
    description: 'Full platform access including the admin panel. Granted via the admin Entra group or env override.',
  },
  {
    id: 'lead',
    name: 'Lead',
    colorKey: 'purple',
    description: 'Sees and edits every view; admin panel and admin-only endpoints stay off-limits.',
  },
  {
    id: 'analyst',
    name: 'Analyst',
    colorKey: 'textSecondary',
    description: `Default staff tier. Sees: ${sectionSummary('analyst')}. Anything more comes from custom role grants below.`,
  },
  {
    id: 'customer',
    name: 'Customer',
    colorKey: 'blue',
    description: 'External B2B guest — Customer Portal only, scoped server-side to their own customer space.',
  },
];
